import { useState } from 'react'
import { Icon, Stack, Text } from '@/design-system'
import { cn } from '@/lib/cn'
import type { Card as CardModel, WalletId } from '@/data/types'
import styles from './WalletRow.module.css'

/**
 * Third-party wallet marks, drawn rather than imported: these are used to say
 * "this card works here", which is what the marks are for.
 */
function AppleGlyph() {
  return (
    <svg className={styles.glyph} width="15" height="18" viewBox="0 0 15 18" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12.3 9.5c0-2 1.6-3 1.7-3-.9-1.4-2.4-1.5-2.9-1.6-1.2-.1-2.4.7-3 .7-.6 0-1.6-.7-2.6-.7-1.3 0-2.6.8-3.2 2C.9 9.4 2 13 3.2 15c.6 1 1.3 2 2.3 2 .9 0 1.2-.6 2.3-.6s1.4.6 2.4.6c1 0 1.6-1 2.2-1.9.7-1.1 1-2.2 1-2.2s-1.9-.7-2-2.9ZM10.5 3.4c.5-.6.9-1.5.8-2.4-.8 0-1.7.5-2.2 1.2-.5.6-.9 1.5-.8 2.4.9.1 1.7-.5 2.2-1.2Z"
      />
    </svg>
  )
}

function GoogleGlyph() {
  return (
    <svg className={styles.glyph} width="17" height="17" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.6 9.2c0-.6-.1-1.3-.2-1.9H9v3.5h4.8a4.1 4.1 0 0 1-1.8 2.7v2.3h2.9c1.7-1.6 2.7-3.9 2.7-6.6Z" />
      <path fill="#34A853" d="M9 18c2.4 0 4.5-.8 6-2.2l-2.9-2.3c-.8.6-1.9.9-3.1.9-2.4 0-4.4-1.6-5.1-3.8H.9v2.3A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.9 10.7a5.4 5.4 0 0 1 0-3.4V5H.9a9 9 0 0 0 0 8l3-2.3Z" />
      <path fill="#EA4335" d="M9 3.6c1.3 0 2.5.5 3.4 1.3l2.6-2.6A9 9 0 0 0 .9 5l3 2.3C4.6 5.2 6.6 3.6 9 3.6Z" />
    </svg>
  )
}

function SamsungGlyph() {
  return (
    <svg className={styles.glyph} width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <circle cx="9" cy="9" r="8" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path
        fill="currentColor"
        d="M5.4 11.6c.4.5 1.1.8 1.9.8 1 0 1.7-.5 1.7-1.2 0-.6-.4-.9-1.4-1.2l-.8-.2C5.6 9.4 5 8.8 5 7.8 5 6.5 6.1 5.6 7.7 5.6c1 0 1.8.3 2.3.9l-.8.8c-.4-.4-.9-.6-1.5-.6-.8 0-1.4.4-1.4 1 0 .5.4.8 1.3 1l.8.3c1.3.3 1.9 1 1.9 2 0 1.4-1.1 2.3-2.9 2.3-1.1 0-2.1-.4-2.7-1.1l.7-.6ZM11.6 5.8h1.2v6.4h-1.2z"
      />
    </svg>
  )
}

const WALLETS: ReadonlyArray<{
  readonly id: WalletId
  readonly label: string
  readonly glyph: () => React.ReactElement
}> = [
  { id: 'apple', label: 'Apple Pay', glyph: AppleGlyph },
  { id: 'google', label: 'Google Pay', glyph: GoogleGlyph },
  { id: 'samsung', label: 'Samsung Pay', glyph: SamsungGlyph },
]

export function WalletRow({ card }: { readonly card: CardModel }) {
  const [added, setAdded] = useState<readonly WalletId[]>(card.wallets)

  /*
   * Our issuer provisions wallets from the physical Mastercard only. Showing
   * "Add to Apple Pay" on a virtual or agent card would promise something the
   * contract does not deliver.
   */
  const eligible = card.kind === 'physical' && card.network === 'mastercard'

  return (
    <div>
      <div className={styles.row}>
        {WALLETS.map((wallet) => {
          const on = added.includes(wallet.id)
          const Glyph = wallet.glyph
          return (
            <button
              key={wallet.id}
              type="button"
              className={cn(styles.wallet, on && styles.added)}
              disabled={!eligible || on}
              aria-pressed={on}
              onClick={() => setAdded((current) => [...current, wallet.id])}
            >
              <Glyph />
              <Text variant="caption" weight="semibold">
                {on ? 'Added' : 'Add'}
              </Text>
              <Text variant="caption" tone="secondary">
                {wallet.label}
              </Text>
              {on && <Icon name="check_circle" size={16} filled />}
            </button>
          )
        })}
      </div>

      {!eligible && (
        <Stack direction="row" align="start" gap={2} className={styles.note}>
          <Icon name="info" size={16} />
          <Text variant="caption" tone="secondary">
            {card.kind === 'agent'
              ? 'Agent cards do not go in a wallet. They authorise online, inside the limits you set.'
              : 'Wallets are provisioned from your physical Mastercard. Order one and it works on all three.'}
          </Text>
        </Stack>
      )}
    </div>
  )
}
